import { CloneHelper } from '@rightship-tech/common-ui/helpers/clone.helper';
import { BindingEngine, computedFrom, observable } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { I18N } from "aurelia-i18n";
import { Claims } from "../../../../constants/authentication/claims";
import { MobileHelper } from "../../../../helpers/mobile-helper";
import { DataGridColumn, DataType, QueryFilter, SourceType } from "../../../../models/queries/queries.models";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { RatingItem } from "../../../components/rating-selector/rating-selector";
import { CustomGridPageBase } from "../../common/custom-grid-page.base";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { LookupValueRequest } from "../../../../models/lookups/lookup.models";
import { RightShipMultiLookupModels, MultiLookupModel } from "../../../components/multi-checked-list-selector/multi-checked-list-selector";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { RangeSelectorHelper } from "../../../components/range-selector/range-selector";
import KeyDownEvent = JQuery.KeyDownEvent;
import { LookupClientService } from '../../../../services/lookups/lookup-client.service';
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { Guid } from '../../../../utilities/guid';
import { CustomerModel } from '../../../../models/customers/customer.models';
import { Messages } from '../../../../configuration/event-messages.constants';
import { EventAggregator } from 'aurelia-event-aggregator';
import { ManageRightShipUsersClientService } from "../../../../services/modules/righship-qi-manage-users.service";
import { SubscriptionProductCodes } from '../../../../models/customers/customer-subscription.models';
import { PlatformFeatureKeys } from "@rightship-tech/common-ui/dialogs/upgrade-from-freemium/freemium-feature-info-model";
import { Reflection } from '../../../../utilities/reflection';

@autoinject()
@transient()
export class Vessels extends CustomGridPageBase {

    public isMobileFilterOpen: boolean = false;
    @observable()
    public filter: string;
    public appliedFilter: string;
    public defaultPageCount: number = 100;
    public getItemRoute = (id) => `/vessel/${id}`;
    public rightShipUser: boolean;
    public initialFilters: QueryFilter[];
    private odataUrl = "odata/Vessels";
    public showSafetyScore: boolean = false;
    public showGhg: boolean = false;
    public canAddVessel: boolean;
    public selectedFilter: { title: string, clear: () => void, reset: () => void, apply: () => void };
    public customer: CustomerModel;
    public isLimitedSubscription: boolean;

    private readonly defaultStatusGroupFilters: string[] = ["in service"];

    constructor(i18N: I18N, protected bindingEngine: BindingEngine, private userClaimsService: UserClaimsService,
        private lookupService: LookupClientService, private dialogClientService: DialogClientService,
        private eventAggregator: EventAggregator, private manageRightShipUsersClientService: ManageRightShipUsersClientService) {
        super(i18N, "Vessel", bindingEngine);
        this.rightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canAddVessel = this.userClaimsService.hasPermission(Claims.CanAddVessel);
        this.showSafetyScore = this.userClaimsService.hasPermission(Claims.ShowSafetyScore);
        this.showGhg = this.userClaimsService.hasPermission(Claims.ShowGHG);

        this.columns.push();
        this.initialSorting = this.getSorting();
    }

    async activate() {
        try {
            this.isLimitedSubscription = await this.manageRightShipUsersClientService.isLimitedCustomerSubscription(SubscriptionProductCodes.informationServices);

            await this.setDefaultFilters()
        } catch (e) {
            console.error(e);
        }
        finally {
            this.setItemSource();
        }
    }

    async setDefaultFilters() {
        const x = await this.lookupService.getLookup(new LookupValueRequest(LookupValueQuery.VesselStatusGroups));
        const values = x.activeValues;

        if (values && values.length) {
            const lookup = LookupValueQuery.VesselStatuses;
            const ids = values.filter(v => this.defaultStatusGroupFilters.indexOf(v.name.trim().toLowerCase()) > -1).map(v => v.id);
            const res = await RightShipMultiLookupModels.setSelectedInMultiLookupByIds(LookupValueQuery.VesselStatuses, this.lookupService, ids, LookupValueQuery.VesselStatusGroups);
            this.mclFilters[lookup] = res.filters;
            this.vesselStatusDisplayText = this.i18N.tr('vessel-status-text') + ' ' + res.filterDisplayText.trim();
            this.vesselStatusDescription = res.filterDescription;
        }
    }

    public closeAllFilters() {
        this.showSafetyRatingFilter =
            this.showGhgRatingFilter =
            this.showCiiRatingFilter =
            this.showAgeFilter =
            this.showDwtFilter =
            this.showClassFilter =
            this.showStateFilter =
            this.showFlagFilter = false;
    }

    //#region rating filter
    public showSafetyRatingFilter: boolean = false;
    public selectedSafetyRatings: number[] = [];
    public mobileSafetyRatings: number[] = [];

    public toggleSafetyRatingFilters() {

        //If freemium send event, open dialog and return
        if (this.isLimitedSubscription) {
            this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vessels);
            return;
        }

        if (!this.showSafetyRatingFilter) {
            this.closeAllFilters();
        }
        this.showSafetyRatingFilter = !this.showSafetyRatingFilter;
        this.selectedFilter = {
            title: "Safety Score",
            clear: () => {
                this.clearSafetyRatingFilter();
            },
            reset: () => {
                this.mobileSafetyRatings = CloneHelper.cloneObject(this.selectedSafetyRatings);
            },
            apply: () => {
                this.applySafetyRatingFilter(this.mobileSafetyRatings.map(r => new RatingItem(true, r, r == -1 ? -1 : -3))); //if rating is -1 we set the rating type to Na (-1) and if its not -1 we set it to full rating (-3)
            }
        }
    }

    public applySafetyRatingFilter(ratings: RatingItem[]) {
        this.selectedSafetyRatings = [];

        if (ratings && ratings.length > 0) {
            this.selectedSafetyRatings = this.selectedSafetyRatings.concat(ratings.map(x => x.rating));
        }

        this.setItemSource();

        this.toggleSafetyRatingFilters();
    }

    public clearSafetyRatingFilter() {
        this.selectedSafetyRatings = [];
        this.mobileSafetyRatings = [];
        this.setItemSource();
    }

    @computedFrom("selectedSafetyRatings", "selectedSafetyRatings.length")
    public get safetyRatingFilterActive() {
        return this.selectedSafetyRatings && this.selectedSafetyRatings.length > 0;
    }

    //#endregion

    //#region ghg rating
    public selectedGhgRatingWeights: number[] = [];
    public mobileGhgRatingWeights: number[] = [];
    public showGhgRatingFilter: boolean = false;

    public toggleGhgRatingFilters() {

        //If freemium send event, open dialog and return
        if (this.isLimitedSubscription) {
            this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vessels);
            return;
        }

        if (!this.showGhgRatingFilter) {
            this.closeAllFilters();
        }
        this.showGhgRatingFilter = !this.showGhgRatingFilter;
        this.selectedFilter = {
            title: "GHG Rating",
            clear: () => {
                this.clearGhgRatingFilter();
            },
            reset: () => {
                this.mobileGhgRatingWeights = CloneHelper.cloneObject(this.selectedGhgRatingWeights);
            },
            apply: () => {
                this.applyGhgRating(this.mobileGhgRatingWeights);
            }
        }
    }

    public applyGhgRating(items: number[]) {
        this.selectedGhgRatingWeights = items;
        this.setItemSource();
        this.toggleGhgRatingFilters();
    }

    public clearGhgRatingFilter() {
        this.selectedGhgRatingWeights = [];
        this.mobileGhgRatingWeights = [];
        this.setItemSource();
    }

    @computedFrom("selectedGhgRatingWeights", "selectedGhgRatingWeights.length")
    public get ghgRatingFilterActive() {
        return this.selectedGhgRatingWeights && this.selectedGhgRatingWeights.length > 0;
    }

    //#endregion

    //#region cii rating
    public selectedCiiRatings: string[] = [];
    public mobileCiiRatings: string[] = [];
    public showCiiRatingFilter: boolean = false;

    public toggleCiiRatingFilters() {

        //If freemium send event, open dialog and return
        if (this.isLimitedSubscription) {
            this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vessels);
            return;
        }

        if (!this.showCiiRatingFilter) {
            this.closeAllFilters();
        }
        this.showCiiRatingFilter = !this.showCiiRatingFilter;
        this.selectedFilter = {
            title: "CII Rating",
            clear: () => {
                this.clearCiiRatingFilter();
            },
            reset: () => {
                this.mobileCiiRatings = CloneHelper.cloneObject(this.selectedCiiRatings);
            },
            apply: () => {
                this.applyCiiRating(this.mobileCiiRatings);
            }
        }
    }

    public applyCiiRating(items: string[]) {
        this.selectedCiiRatings = items;
        this.setItemSource();
        this.toggleCiiRatingFilters();
    }

    public clearCiiRatingFilter() {
        this.selectedCiiRatings = [];
        this.mobileCiiRatings = [];
        this.setItemSource();
    }

    @computedFrom("selectedCiiRatings", "selectedCiiRatings.length")
    public get ciiRatingFilterActive() {
        return this.selectedCiiRatings && this.selectedCiiRatings.length > 0;
    }

    //#endregion

    //#region vessel status filter
    public vesselStatusDisplayText: string = this.i18N.tr('vessel-status-text');
    public vesselStatusDescription: string = "";

    public clearVesselStatusFilter(reload: boolean = true) {
        const lookup = LookupValueQuery.VesselStatuses;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1)
            this.mclFilters[lookup] = [];
        this.vesselStatusDescription = "";
        this.vesselStatusDisplayText = this.i18N.tr('vessel-status-text');
        if (reload)
            this.setItemSource();
    }

    @computedFrom("mclFilters", "mclFilters.RS_VesselStatuses", "mclFilters.RS_VesselStatuses.length")
    public get vesselStatusFilterActive() {
        const lookup = LookupValueQuery.VesselStatuses;
        return (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0);
    }
    //#endregion

    //#region vessel type filter
    public vesselTypeDisplayText: string = this.i18N.tr('vessel-type');
    public vesselTypeDescription: string = "";

    public clearVesselTypeFilter(reload: boolean = true) {
        const lookup = LookupValueQuery.VesselTypes;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1)
            this.mclFilters[lookup] = [];
        this.vesselTypeDescription = "";
        this.vesselTypeDisplayText = this.i18N.tr('vessel-type');
        if (reload)
            this.setItemSource();
    }

    @computedFrom("mclFilters", "mclFilters.RS_VesselTypes", "mclFilters.RS_VesselTypes.length")
    public get vesselTypeFilterActive() {
        const lookup = LookupValueQuery.VesselTypes;
        return (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0);
    }
    //#endregion

    //#region dwt filter
    public dwtFirstFilter: number;
    public dwtSecondFilter: number;
    public mobileDwtFilter: { low: number, high: number };
    public dwtDefaults: { low: number, high: number } = { low: 0, high: 565000 };
    public showDwtFilter: boolean = false;
    public dwtIsApplied: boolean = false;

    public toggleDwtFilters() {
        if (!this.showDwtFilter) {
            this.closeAllFilters();
        }
        this.showDwtFilter = !this.showDwtFilter;
        this.selectedFilter = {
            title: "DWT",
            clear: () => {
                this.clearDwtFilter();
            },
            reset: () => {
                this.mobileDwtFilter = RangeSelectorHelper.resetRangeFilter(this.dwtFirstFilter, this.dwtSecondFilter, this.dwtDefaults);
            },
            apply: () => {
                this.applyDwtFilter(this.mobileDwtFilter);
            }
        }
    }

    public applyDwtFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.dwtFirstFilter = filterValue.low;
            this.dwtSecondFilter = filterValue.high;
            this.dwtIsApplied = true;
        }

        this.setItemSource();
        this.toggleDwtFilters();
    }

    public clearDwtFilter() {
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.mobileDwtFilter = { low: null, high: null };
        this.dwtIsApplied = false;
        this.setItemSource();
    }

    @computedFrom("dwtIsApplied", "dwtFirstFilter", "dwtSecondFilter")
    public get dwtFilterActive() {
        return this.dwtIsApplied && ((this.dwtFirstFilter && this.dwtFirstFilter >= this.dwtDefaults.low) || (this.dwtSecondFilter && this.dwtSecondFilter <= this.dwtDefaults.high));
    }

    //#endregion

    //#region Multi Checked List dialog filters
    public cachedMultipleLookups: { [lookup: string]: MultiLookupModel[] } = {};
    public mclFilters: { [lookup: string]: QueryFilter[] } = {};

    openMCLDialog = (type: string) => {
        let lookup = null;
        if (type == "vessel_types")
            lookup = LookupValueQuery.VesselTypes;
        else if (type == "vessel_statuses")
            lookup = LookupValueQuery.VesselStatuses;
        else {
            console.error("invalid multi filter type")
        }
        const displayText = type == "vessel_types" ? this.i18N.tr('vessel-type') : this.i18N.tr('vessel-status-text');

        this.closeAllFilters();

        const cachedMultiLookups = this.cachedMultipleLookups[lookup] ? this.cachedMultipleLookups[lookup] : RightShipMultiLookupModels.getMultiLookupModel(lookup);

        let filterValues: { [filterProperty: string]: number[] } = null;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0) {
            filterValues = {};
            this.mclFilters[lookup].forEach(x => {
                filterValues[x.filterProperty.name] = x.filterValue;
            });
        }

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.MultiCheckedListDialog, {
            multiLookups: cachedMultiLookups,
            title: displayText,
            selectedIds: filterValues,
            onApply: (multiLookups: MultiLookupModel[]) => {

                this.mclFilters[lookup] = [];

                const result = RightShipMultiLookupModels.getMultiLookupFilters(lookup, displayText, multiLookups);

                this.mclFilters[lookup] = result.filters;

                if (lookup == LookupValueQuery.VesselTypes) {
                    this.vesselTypeDisplayText = result.filterDisplayText;
                    this.vesselTypeDescription = result.filterDescription;
                }
                if (lookup == LookupValueQuery.VesselStatuses) {
                    this.vesselStatusDisplayText = result.filterDisplayText;
                    this.vesselStatusDescription = result.filterDescription;
                }

                this.cachedMultipleLookups[lookup] = multiLookups;

                this.setItemSource();
            }
        }, true, false, false, 'right');
    }

    //#endregion

    //#region age filter

    public ageFirstFilter: number;
    public ageSecondFilter: number;
    public mobileAgeFilter: { low: number, high: number };
    public ageDefaults: { low: number, high: number } = { low: 0, high: 100 };
    public showAgeFilter: boolean = false;
    public ageIsApplied: boolean = false;

    public toggleAgeFilters() {
        if (!this.showAgeFilter) {
            this.closeAllFilters();
        }
        this.showAgeFilter = !this.showAgeFilter;
        this.selectedFilter = {
            title: "Age",
            clear: () => {
                this.clearAgeFilter();
            },
            reset: () => {
                this.mobileAgeFilter = RangeSelectorHelper.resetRangeFilter(this.ageFirstFilter, this.ageSecondFilter, this.ageDefaults);
            },
            apply: () => {
                this.applyAgeFilter(this.mobileAgeFilter);
            }
        }
    }

    public applyAgeFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.ageFirstFilter = filterValue.low;
            this.ageSecondFilter = filterValue.high;
            this.ageIsApplied = true;
        }

        this.setItemSource();
        this.toggleAgeFilters();
    }

    public clearAgeFilter() {
        this.ageFirstFilter = null;
        this.ageSecondFilter = null;
        this.mobileAgeFilter = { low: null, high: null };
        this.ageIsApplied = false;
        this.setItemSource();
    }

    @computedFrom("ageIsApplied", "ageFirstFilter", "ageSecondFilter")
    public get ageFilterActive() {
        return this.ageIsApplied && ((this.ageFirstFilter && this.ageFirstFilter >= this.ageDefaults.low) || (this.ageSecondFilter && this.ageSecondFilter <= this.ageDefaults.high));
    }

    //#endregion

    //#region flags filter
    public selectedFlags: number[] = [];
    public mobileFlags: number[] = [];
    public showFlagFilter: boolean = false;
    public flags;

    public toggleFlagFilters() {
        if (!this.showFlagFilter) {
            this.closeAllFilters();
        }
        this.showFlagFilter = !this.showFlagFilter;
        this.selectedFilter = {
            title: "Flags",
            clear: () => {
                this.clearFlagFilter();
            },
            reset: () => {
                this.mobileFlags = CloneHelper.cloneObject(this.selectedFlags);
            },
            apply: () => {
                this.applyFlags(this.mobileFlags.map(r => { return { id: r }; }));
            }
        }
    }

    public applyFlags(items: any[]) {
        this.selectedFlags = items.map(x => x.id);
        this.setItemSource();
        this.toggleFlagFilters();
    }

    public clearFlagFilter() {
        this.selectedFlags = [];
        this.mobileFlags = [];
        this.setItemSource();
    }

    @computedFrom("selectedFlags", "selectedFlags.length")
    public get flagFilterActive() {
        return this.selectedFlags && this.selectedFlags.length > 0;
    }

    //#endregion

    //#region class filter
    public selectedClasses: number[] = [];
    public mobileClasses: number[] = [];
    public showClassFilter: boolean = false;
    public classes;

    public toggleClassFilters() {
        if (!this.showClassFilter) {
            this.closeAllFilters();
        }
        this.showClassFilter = !this.showClassFilter;
        this.selectedFilter = {
            title: "Classes",
            clear: () => {
                this.clearClassFilter();
            },
            reset: () => {
                this.mobileClasses = CloneHelper.cloneObject(this.selectedClasses);
            },
            apply: () => {
                this.applyClasses(this.mobileClasses.map(r => { return { id: r }; }));
            }
        }
    }

    public applyClasses(items: any[]) {
        this.selectedClasses = items.map(x => x.id);
        this.setItemSource();
        this.toggleClassFilters();
    }

    public clearClassFilter() {
        this.selectedClasses = [];
        this.mobileClasses = [];
        this.setItemSource();
    }

    @computedFrom("selectedClasses", "selectedClasses.length")
    public get classFilterActive() {
        return this.selectedClasses && this.selectedClasses.length > 0;
    }

    //#endregion

    //#region state filter
    public selectedStates: number[] = [];
    public mobileStates: number[] = [];
    public showStateFilter: boolean = false;
    public states;

    public toggleStateFilters() {

        //If freemium send event, open dialog and return
        if (this.isLimitedSubscription) {
            this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vessels);
            return;
        }

        if (!this.showStateFilter) {
            this.closeAllFilters();
        }
        this.showStateFilter = !this.showStateFilter;
        this.selectedFilter = {
            title: "Classes",
            clear: () => {
                this.clearClassFilter();
            },
            reset: () => {
                this.mobileStates = CloneHelper.cloneObject(this.selectedStates);
            },
            apply: () => {
                this.applyClasses(this.mobileStates.map(r => { return { id: r }; }));
            }
        }
    }

    public applyStates(items: any[]) {
        this.selectedStates = items.map(x => x.id);
        this.setItemSource();
        this.toggleStateFilters();
    }

    public clearStateFilter() {
        this.selectedStates = [];
        this.mobileStates = [];
        this.setItemSource();
    }

    @computedFrom("selectedStates", "selectedStates.length")
    public get stateFilterActive() {
        return this.selectedStates && this.selectedStates.length > 0;
    }

    //#endregion


    //Mobile Sorting
    public showColumnSorting: boolean = false;
    public selectedColumnSorting: number[] = [];

    public getSorting() {
        let columns: DataGridColumn[] = [];
        columns = [
            {
                columnOrder: -1,
                sortOrder: -1,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: false,
                isHidden: true,
                name: 'safetyScoreSortKey',
                title: 'Safety Rating'
            },
            {
                columnOrder: -1,
                sortOrder: -1,
                dataType: DataType.Number,
                isSortable: true,
                isAscending: false,
                isHidden: true,
                name: 'ghgWeightSortKey',
                title: 'GHG'
            },
            {
                columnOrder: -1,
                sortOrder: -1,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'name',
                title: 'Name'
            }]

        return columns;
    }

    private getItemSource(filters) {
        this.initialFilters = filters;
        this.columns = this.getColumns();
        return {
            sourceType: SourceType.ODataUrl,
            source: this.odataUrl + (this.selectedClasses.length > 0 ? '?filterClassificationSocietyIds=' + this.selectedClasses.join(",") : ''),
            columns: this.columns,
            initialFilters: this.initialFilters,
            initialSorting: this.initialSorting
        };
    }

    private setItemSource() {

        let initialFilters = [];

        initialFilters.push(QueryFilter.getQuery("name", this.filter, "contains({0}, '{1}')", DataType.Default, true, false, 'vessel-search-filters', true, true))
        initialFilters.push(QueryFilter.getQuery("internationalMaritimeOrganizationNumber", this.filter, "contains({0}, '{1}')", DataType.Default, false, false, 'vessel-search-filters', true, true))
        initialFilters.push(QueryFilter.getQuery("europeanNumberOfIdentification", this.filter, "contains({0}, '{1}')", DataType.Default, false, false, 'vessel-search-filters', true, true))
        initialFilters.push(QueryFilter.getQuery("vehicleIdentificationNumber", this.filter, "contains({0}, '{1}')", DataType.Default, false, false, 'vessel-search-filters', true, true))

        //#region safety rating filter
        if (this.selectedSafetyRatings && this.selectedSafetyRatings.length > 0) {
            const guid = Guid.generateGuid();
            const hasSafetyRating: boolean = this.selectedSafetyRatings.filter(x => x >= 0).length > 0;
            if (hasSafetyRating) {
                const safetyRatingFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} in ({1})",
                        friendlyName: "in",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: "safetyRating",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: this.selectedSafetyRatings.filter(x => x >= 0),
                    isAnd: true,
                    skipOnNullOrEmpty: true,
                    groupGuid: guid
                };
                initialFilters.push(safetyRatingFilter);
            }

            const isIncludeNa: boolean = this.selectedSafetyRatings.indexOf(-1) >= 0;
            if (isIncludeNa) {
                const naFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} in ({1})",
                        friendlyName: "in",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: "safetyRatingType",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: [-1],
                    isAnd: hasSafetyRating ? false : true,
                    skipOnNullOrEmpty: true,
                    groupGuid: guid
                };
                initialFilters.push(naFilter);
            }
            else if (hasSafetyRating) {
                const naFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} in ({1}) eq false",
                        friendlyName: "not in",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: "safetyRatingType",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: [-1],
                    isAnd: true,
                    skipOnNullOrEmpty: true,
                    groupGuid: guid
                };
                initialFilters.push(naFilter);
            }
        }
        //#endregion

        //#region ghg filter
        if (this.selectedGhgRatingWeights && this.selectedGhgRatingWeights.length > 0) {

            const ghgRatingFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "ghgRatingWeight",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedGhgRatingWeights || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(ghgRatingFilter);
        }
        //#endregion

        //#region cii filter
        if (this.selectedCiiRatings?.length > 0) {
            
            const thisYear = new Date().getFullYear();
            const lastYear = thisYear - 1;
            const ciiRatingCertifiedYears = [thisYear, lastYear, lastYear-1];

            //Ratings which are not 'not certified' are selected
            if(this.selectedCiiRatings.filter(rating => rating != "notCertified").length > 0){

                //Filter by the selected rating
                const ciiRatingFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} in ({1})",
                        friendlyName: "in",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: "ciiRating",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: this.selectedCiiRatings || [],
                    isAnd: true,
                    skipOnNullOrEmpty: true,
                };   

                //Filter by certified rating years
                const ciiRatingYearFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} in ({1})",
                        friendlyName: "in",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: "ciiRatingYear",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: ciiRatingCertifiedYears || [],
                    isAnd: true,
                    skipOnNullOrEmpty: true,
                };

                initialFilters.push(ciiRatingFilter);
                initialFilters.push(ciiRatingYearFilter);
            }

            //Not certified is selected
            if(this.selectedCiiRatings.filter(rating => rating == "notCertified").length > 0){

                //Filter by not certified rating years
                const ciiRatingYearFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} in ({1}) eq false",
                        friendlyName: "in",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: "ciiRatingYear",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: ciiRatingCertifiedYears || [],
                    isAnd: false,
                    skipOnNullOrEmpty: true,
                };
    
                initialFilters.push(ciiRatingYearFilter);
            } 
        }
        //#endregion

        //#region vessel type filter
        if (this.mclFilters.RS_VesselTypes && this.mclFilters.RS_VesselTypes.length > 0) {
            initialFilters = initialFilters.concat(this.mclFilters.RS_VesselTypes)
        }
        //#endregion

        //#region dwt values
        if (this.dwtFirstFilter || this.dwtSecondFilter) {
            initialFilters.push(QueryFilter.getQuery('dwt', this.dwtFirstFilter ? this.dwtFirstFilter : 0, 'ge', DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery('dwt', this.dwtSecondFilter ? this.dwtSecondFilter : 0, 'le', DataType.Number, true));
        }
        //#endregion

        //#region age values
        if (this.ageFirstFilter || this.ageSecondFilter) {
            initialFilters.push(QueryFilter.getQuery('age', this.ageFirstFilter ? this.ageFirstFilter : 0, 'ge', DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery('age', this.ageSecondFilter ? this.ageSecondFilter : 0, 'le', DataType.Number, true));
        }
        //#endregion

        //#region vessel status filter
        if (this.mclFilters.RS_VesselStatuses && this.mclFilters.RS_VesselStatuses.length > 0) {
            initialFilters = initialFilters.concat(this.mclFilters.RS_VesselStatuses)
        }
        //#endregion

        //#region flag filter
        if (this.selectedFlags && this.selectedFlags.length > 0) {
            const flagFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "flagId",
                    isDynamicProperty: false,
                    type: DataType.Lookup,
                    lookupMappedToProperty: LookupValueQuery.Flags,
                    isNullable: true,
                },
                filterValue: this.selectedFlags || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(flagFilter);
        }
        //#endregion
        
         //#region state filter
         if (this.selectedStates && this.selectedStates.length > 0) {
            const selStates = [...this.selectedStates]
            if (selStates.some(h=>h==1) && !selStates.some(h=>h==2)) //Valid RS Inspection
                selStates.push(2); //should also display Requires RS Inspection in less than 60 days
            const classFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "inspectionState",
                    isDynamicProperty: false,
                    type: DataType.Lookup,
                    lookupMappedToProperty: LookupValueQuery.InspectionStates,
                    isNullable: true,
                },
                filterValue: selStates || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(classFilter);
        }
        //#endregion

        this.itemSource = this.getItemSource(initialFilters);
    }

    clearFilter() {
        this.filter = "";
        this.appliedFilter = this.filter;

        this.setItemSource();
    }

    clearAllFilters($event, reload: boolean = true) {
        if ($event)
            $event.stopPropagation();

        this.appliedFilter = null;
        this.filter = null;

        this.clearSafetyRatingFilter();
        this.clearGhgRatingFilter();
        this.clearCiiRatingFilter();
        this.clearVesselTypeFilter();
        this.clearDwtFilter();
        this.clearAgeFilter();
        this.clearVesselStatusFilter();
        this.clearFlagFilter();
        this.clearClassFilter();
        this.clearStateFilter();

        this.closeAllFilters();

        if (reload)
            this.setItemSource();
    }

    public getCompanyItemRoute = (id) => `/vessel/${id}`;

    getColumns(): DataGridColumn[] {
        return [
            {
                dataType: DataType.CustomTemplate,
                value: '<div class="column">' +
                    '<a class="link" href="/vessel/${item.vesselId}">${item.name}</a>' +
                    '<span if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span>' +
                    '</div>',
                name: "name",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Name",
                instancePerRow: true,
                sortOrder: 2,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null
                }
            },
            {
                dataType: DataType.CustomTemplate,
                value: '<comp-safety-score score.bind="item.safetyRating" inspection.bind="item" safety-rating-indicative.bind="item.safetyRatingIndicative" safety-score-type.bind="item.safetyRatingType" incident-grace-periods.bind="getIncidentGracePeriods(item)"' +
                    'has-error.bind="item.hasError" is-condensed="true" unlock-feature-key="vessels" is-sanctioned-or-geopolitical-zone-or-flag-fake.bind="item.isSanctioned || item.isFlagFake" locked-feature.bind="lockedFeature" mask-data.bind="maskData(item.safetyRating)"></comp-safety-score>',
                name: "safetyRating",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Safety Score",
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: 1,
                isHidden: !this.showSafetyScore,
                isSortable: true,
                parameter: {
                    item: null,
                    lockedFeature: this.isLimitedSubscription,
                    maskData: (safetyRating) => {
                        return safetyRating === undefined;
                    },
                    getIncidentGracePeriods: (item) => {
                        return item.incidentGracePeriodJsonData ? JSON.parse(item.incidentGracePeriodJsonData) : [];
                    }
                },
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "220px"
            },
            {
                dataType: DataType.CustomTemplate,
                value: '<comp-ghg-rating rating.bind="item.ghgRating" rating-plus.bind="item.isGhgRatingPlus" ' +
                    'is-verified.bind="item.isGhgRatingVerified" is-eexi-applicable.bind="item.isEexiApplicable" is-eexi-compliant.bind="item.isEexiCompliant" ' +
                    'eexi-compliance-type.bind="item.eexiComplianceTypeId" show-eexi-banner.bind="true" unlock-feature-key="vessels" locked-feature.bind="lockedFeature" mask-data.bind="maskData(item.ghgRating)"></comp-ghg-rating>',
                name: "ghgRatingWeight",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "GHG Rating",
                instancePerRow: true,
                sortOrder: 2,
                columnOrder: 2,
                isHidden: !this.showGhg,
                isSortable: true,
                parameter: {
                    item: null,
                    lockedFeature: this.isLimitedSubscription,
                    maskData: (ghgRating) => {
                        return ghgRating === undefined;
                    }
                },
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "140px"
            },
            {
                dataType: DataType.CustomTemplate,
                value: '<comp-cii-rating rating.bind="item.ciiRating" rating-year.bind="item.ciiRatingYear"' +
                    'unlock-feature-key="vessels" locked-feature.bind="lockedFeature" mask-data.bind="maskData(item.ciiRating)"></comp-cii-rating>',
                name: "ciiRating",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "CII Rating",
                instancePerRow: true,
                sortOrder: 3,
                columnOrder: 3,
                isSortable: true,
                parameter: {
                    item: null,
                    lockedFeature: this.isLimitedSubscription,
                    maskData: (ciiRating) => {
                        return ciiRating === undefined;
                    }
                },
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "168px"
            },
            {
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.Default,
                name: 'shipType',
                title: 'Vessel Type',
                isSortable: true
            },
            {
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                value: "<span>${item.dwt.toLocaleString()}</span>",
                name: 'dwt',
                title: 'DWT',
                isSortable: true,
                instancePerRow: true,
                parameter: {
                    item: null
                },
            },
            {
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.CustomTemplate,
                value: "<span>${item.age}y/o (${item.yearOfConstruction})</span>",
                // instancePerRow: true,
                name: 'age',
                title: 'Age',
                isSortable: true,
                instancePerRow: true,
                parameter: {
                    item: null
                },
            },
            {
                columnOrder: 7,
                sortOrder: 7,
                dataType: DataType.Default,
                name: 'shipStatus',
                title: 'Vessel Status',
                isSortable: true
            },
        ];
    }

    public toggleColumnSortingFilters() {
        if (!this.showColumnSorting) {
            this.closeAllFilters();
        }
        this.showColumnSorting = !this.showColumnSorting;
        this.selectedFilter = {
            title: "Column sorting",
            clear: () => {
            },
            apply: () => {
                this.applyColumnSortingFilter(this.selectedColumnSorting.map(bId => {
                    return { id: bId };
                },), false);
            },
            reset: () => {
            },
        }
    }

    sortClass(column: DataGridColumn) {
        if (column.isAscending) {
            return "Ascending"
        }
        else if (column.isAscending == false) {
            return "Descending"
        }
        else {
            return "";
        }
    }

    toggleMobileFilterDialog() {
        this.isMobileFilterOpen = !this.isMobileFilterOpen;
    }

    public applyColumnSortingFilter(items: any[], reload: boolean = true) {
        this.selectedColumnSorting = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleColumnSortingFilters();
    }


    public goBackToFilters() {
        if (this.selectedFilter)
            this.selectedFilter.reset();
        this.closeAllFilters();
        this.selectedFilter = null;
    }

    onMobileApply() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
            this.closeAllFilters();
        }
        else {
            this.closeAllFilters();
            this.toggleMobileFilterDialog();
        }
    }

    public get isSubFilterOpen(): boolean {
        return this.showColumnSorting;
    }

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
        }
    }

    sortByMobile(column: DataGridColumn) {
        this.dataGridVM.sortingTriggered(column);
    }
    
    @computedFrom(Reflection.getPropertyPath<Vessels>(z => z.showSafetyRatingFilter),
    Reflection.getPropertyPath<Vessels>(z => z.showGhgRatingFilter),
    Reflection.getPropertyPath<Vessels>(z => z.showCiiRatingFilter),
    Reflection.getPropertyPath<Vessels>(z => z.showDwtFilter),
    Reflection.getPropertyPath<Vessels>(z => z.showAgeFilter),
    Reflection.getPropertyPath<Vessels>(z => z.showStateFilter),
    Reflection.getPropertyPath<Vessels>(z => z.showClassFilter),
    Reflection.getPropertyPath<Vessels>(z => z.showFlagFilter))
    public get mobileFilterSelected() {
        return this.showSafetyRatingFilter || this.showGhgRatingFilter || this.showCiiRatingFilter
            || this.showDwtFilter || this.showAgeFilter || this.showStateFilter || this.showClassFilter || this.showFlagFilter;
    }


    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    add() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddVesselDialog, {}, true, false, false, 'right');
    }

    unlockFeature() {
        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vessels);
    }
}
